/**
 * SKI AXIS · Cohort 1 Application — Institutional, governance-first form.
 *
 * Mounted at /apply (and /cohort-1/apply for backward-compat with existing
 * homepage CTAs). Submits to POST /api/cohort/applications. The backend
 * Pydantic model accepts both legacy (YC-style) and new institutional fields,
 * so this form coexists with the older /apply-form route.
 *
 * Design: Light Editorial — cream (#FAF7F0) + navy (#0D1B2A) + gold (#C8A84B).
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, ArrowRight, Loader2, Shield, Sparkles, Globe2,
  AlertCircle, ScrollText,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.app-root{background:#FAF7F0;min-height:100vh;color:#0D1B2A;font-family:'DM Sans',-apple-system,sans-serif;}
.app-shell{max-width:980px;margin:0 auto;padding:48px 28px 96px;}
@media(max-width:680px){.app-shell{padding:32px 18px 64px;}}

/* URGENCY BAR */
.app-urgent{background:linear-gradient(90deg,#0D1B2A 0%,#1A2F4A 50%,#0D1B2A 100%);color:#FAF7F0;padding:14px 24px;text-align:center;font-size:11.5px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;border-bottom:2px solid #C8A84B;}
.app-urgent em{color:#C8A84B;font-style:normal;font-weight:800;}

/* HERO */
.app-hero{padding:56px 0 36px;border-bottom:1px solid rgba(13,27,42,.08);margin-bottom:48px;}
.app-eyebrow{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:18px;}
.app-eyebrow::before{content:'';width:32px;height:1px;background:#C8A84B;}
.app-h1{font-family:'Cormorant Display',serif;font-size:clamp(40px,5.5vw,62px);font-weight:700;line-height:1.02;letter-spacing:-.02em;margin:0 0 18px;color:#0D1B2A;}
.app-h1 em{font-style:italic;color:#C8A84B;font-weight:600;}
.app-subline{font-size:17px;line-height:1.6;color:#3D4A5C;max-width:680px;margin:0;font-weight:300;}

/* WHY APPLY */
.app-why{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-bottom:48px;}
@media(max-width:760px){.app-why{grid-template-columns:1fr;}}
.app-why-card{background:#fff;border:1px solid #E8DFC9;border-left:3px solid #C8A84B;padding:22px 24px;display:flex;align-items:flex-start;gap:14px;}
.app-why-icn{flex-shrink:0;width:36px;height:36px;border:1px solid #C8A84B;color:#C8A84B;display:flex;align-items:center;justify-content:center;border-radius:50%;}
.app-why-t{font-family:'Cormorant Display',serif;font-size:17px;font-weight:700;color:#0D1B2A;line-height:1.3;margin:0;}

/* FORM */
.app-form{background:#fff;border:1px solid #E8DFC9;padding:48px 56px;}
@media(max-width:680px){.app-form{padding:32px 22px;}}
.app-section{margin-bottom:40px;}
.app-section:last-of-type{margin-bottom:0;}
.app-sec-num{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-size:18px;font-weight:600;letter-spacing:.04em;}
.app-sec-h{font-family:'Cormorant Display',serif;font-size:26px;font-weight:700;color:#0D1B2A;line-height:1.2;letter-spacing:-.01em;margin:6px 0 24px;border-bottom:1px solid #E8DFC9;padding-bottom:14px;}

.app-row{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:18px;}
.app-row-1{display:grid;grid-template-columns:1fr;gap:20px;margin-bottom:18px;}
@media(max-width:680px){.app-row{grid-template-columns:1fr;}}

.app-fld{display:flex;flex-direction:column;gap:6px;}
.app-lbl{font-size:11px;font-weight:700;letter-spacing:.2em;color:#3D4A5C;text-transform:uppercase;}
.app-lbl em{color:#C8A84B;font-style:normal;font-weight:800;}
.app-help{font-size:11px;color:#7A8190;font-style:italic;font-family:'Cormorant Display',serif;font-size:13px;}
.app-input,.app-select,.app-area{font-family:inherit;font-size:14px;color:#0D1B2A;background:#FAF7F0;border:1px solid #E8DFC9;padding:13px 14px;outline:none;transition:border-color .15s,background .15s;letter-spacing:.01em;}
.app-input:focus,.app-select:focus,.app-area:focus{border-color:#C8A84B;background:#fff;box-shadow:0 0 0 3px rgba(200,168,75,.08);}
.app-area{min-height:120px;resize:vertical;line-height:1.55;}
.app-radios{display:flex;flex-wrap:wrap;gap:8px;}
.app-radio{cursor:pointer;font-size:12px;font-weight:600;letter-spacing:.04em;color:#3D4A5C;background:#FAF7F0;border:1px solid #E8DFC9;padding:9px 16px;transition:all .15s;}
.app-radio:hover{border-color:#C8A84B;color:#0D1B2A;}
.app-radio input{display:none;}
.app-radio.on{background:#0D1B2A;color:#C8A84B;border-color:#0D1B2A;}

/* SCHOLARSHIP */
.app-schol{background:linear-gradient(135deg,rgba(200,168,75,.08) 0%,rgba(200,168,75,.02) 100%);border:1px dashed rgba(200,168,75,.5);padding:22px 24px;margin-top:24px;display:flex;align-items:flex-start;gap:14px;cursor:pointer;}
.app-schol:hover{border-color:#C8A84B;background:rgba(200,168,75,.12);}
.app-schol input{margin-top:4px;accent-color:#C8A84B;width:16px;height:16px;cursor:pointer;}
.app-schol-t{font-family:'Cormorant Display',serif;font-size:18px;font-weight:700;color:#0D1B2A;margin:0 0 4px;}
.app-schol-d{font-size:12.5px;color:#3D4A5C;line-height:1.55;margin:0;}
.app-schol-d strong{color:#C8A84B;font-weight:700;letter-spacing:.04em;}

/* SUBMIT */
.app-submit{margin-top:36px;width:100%;background:#C8A84B;color:#0D1B2A;border:none;padding:18px 32px;font-family:inherit;font-size:13px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:10px;transition:all .2s;}
.app-submit:hover:not(:disabled){background:#0D1B2A;color:#C8A84B;}
.app-submit:disabled{opacity:.55;cursor:not-allowed;}

.app-foot{margin-top:18px;text-align:center;font-size:12.5px;color:#7A8190;line-height:1.7;font-style:italic;font-family:'Cormorant Display',serif;font-size:14px;}
.app-foot a{color:#C8A84B;text-decoration:none;font-style:normal;font-family:inherit;font-size:13px;font-weight:600;}

.app-err{background:rgba(173,98,64,.08);border-left:3px solid #ad6240;color:#ad6240;padding:14px 18px;margin-bottom:20px;font-size:13px;display:flex;align-items:center;gap:10px;}

/* WHAT HAPPENS NEXT */
.app-next{margin-top:64px;padding:48px 0 12px;border-top:1px solid rgba(13,27,42,.08);}
.app-next-h{font-family:'Cormorant Display',serif;font-size:32px;font-weight:700;line-height:1.1;letter-spacing:-.01em;margin:0 0 30px;color:#0D1B2A;}
.app-next-h em{font-style:italic;color:#C8A84B;}
.app-next-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;}
@media(max-width:760px){.app-next-grid{grid-template-columns:1fr;}}
.app-next-step{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:24px 26px;}
.app-next-num{font-family:'Cormorant Display',serif;font-style:italic;font-size:32px;color:#C8A84B;line-height:1;font-weight:700;}
.app-next-t{font-family:'Cormorant Display',serif;font-size:21px;font-weight:700;color:#0D1B2A;margin:8px 0 6px;line-height:1.2;}
.app-next-d{font-size:13px;color:#3D4A5C;line-height:1.55;margin:0;}

/* SUCCESS */
.app-success{background:#fff;border:1px solid #E8DFC9;border-left:4px solid #C8A84B;padding:64px 56px;text-align:center;}
.app-success-icn{display:inline-flex;width:64px;height:64px;border-radius:50%;background:rgba(200,168,75,.18);border:2px solid #C8A84B;color:#C8A84B;align-items:center;justify-content:center;margin-bottom:18px;}
.app-success-h{font-family:'Cormorant Display',serif;font-size:42px;font-weight:700;color:#0D1B2A;line-height:1.05;letter-spacing:-.015em;margin:0 0 14px;}
.app-success-h em{font-style:italic;color:#C8A84B;}
.app-success-d{font-size:15px;color:#3D4A5C;line-height:1.65;max-width:560px;margin:0 auto 28px;}
.app-success-cta{display:inline-flex;align-items:center;gap:10px;background:#0D1B2A;color:#C8A84B;padding:14px 28px;font-size:11px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;}
.app-success-cta:hover{background:#C8A84B;color:#0D1B2A;}

/* BRING YOUR COHORT — referral card on success state + referrer pill in hero */
.app-byc-card{background:linear-gradient(135deg,rgba(200,168,75,.06) 0%,rgba(200,168,75,.02) 100%);border:1px dashed rgba(200,168,75,.55);padding:32px 36px;margin:0 auto 28px;max-width:640px;text-align:left;}
.app-byc-eb{font-size:10.5px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:#C8A84B;margin-bottom:10px;}
.app-byc-h{font-family:'Cormorant Display',serif;font-size:26px;font-weight:700;line-height:1.15;color:#0D1B2A;margin:0 0 10px;letter-spacing:-.012em;}
.app-byc-h em{font-style:italic;color:#C8A84B;}
.app-byc-d{font-size:13.5px;color:#3D4A5C;line-height:1.6;margin:0 0 18px;}
.app-byc-link{background:#fff;border:1px solid #E8DFC9;display:flex;align-items:center;gap:10px;padding:8px 8px 8px 14px;}
.app-byc-link code{flex:1;font-family:'JetBrains Mono','Courier New',monospace;font-size:12.5px;color:#0D1B2A;letter-spacing:.01em;overflow-x:auto;white-space:nowrap;text-overflow:ellipsis;}
.app-byc-copy{background:#0D1B2A;color:#C8A84B;border:none;padding:9px 18px;font-family:inherit;font-size:10.5px;font-weight:800;letter-spacing:.16em;text-transform:uppercase;cursor:pointer;transition:all .15s;flex-shrink:0;}
.app-byc-copy:hover{background:#C8A84B;color:#0D1B2A;}
.app-byc-foot{font-size:12px;color:#7A8190;margin:14px 0 0;text-align:center;font-style:italic;font-family:'Cormorant Display',serif;font-size:14px;}

/* Referrer pill at the top of the form when ?ref=CODE present */
.app-ref-pill{display:inline-flex;align-items:center;gap:10px;background:rgba(200,168,75,.1);border:1px solid #C8A84B;border-left:3px solid #C8A84B;padding:12px 18px;margin-bottom:24px;}
.app-ref-pill-eb{font-size:10px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:#C8A84B;}
.app-ref-pill-name{font-family:'Cormorant Display',serif;font-style:italic;font-size:18px;font-weight:600;color:#0D1B2A;letter-spacing:-.005em;}
.app-ref-pill-name em{color:#C8A84B;font-style:italic;}
`;

const RADIO = ({ name, value, label, current, onChange }) => (
  <label className={`app-radio ${current === value ? 'on' : ''}`} data-testid={`radio-${name}-${value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <input type="radio" name={name} value={value} checked={current === value} onChange={() => onChange(name, value)} />
    {label}
  </label>
);

const RadioGroup = ({ name, options, value, onChange }) => (
  <div className="app-radios">
    {options.map((opt) => (
      <RADIO
        key={typeof opt === 'string' ? opt : opt.value}
        name={name}
        value={typeof opt === 'string' ? opt : opt.value}
        label={typeof opt === 'string' ? opt : opt.label}
        current={value}
        onChange={onChange}
      />
    ))}
  </div>
);

export default function CohortApplication() {
  const navigate = useNavigate();
  useSEO({
    title: 'Apply for SKI Axis Cohort 1 · Governance-First Accelerator · July 2026',
    description: 'Apply to SKI Axis — UAE\'s first governance-first startup accelerator. 20 founders per cohort. AI-scored applications. AED 6,900 fee. Cohort 1 launches July 2026. 5 fully-funded scholarships for women founders.',
  });

  const [form, setForm] = useState({
    founder_name: '',
    founder_email: '',
    whatsapp: '',
    country_residence: '',
    linkedin_url: '',
    company_name: '',
    industry: '',
    company_stage: '',
    country_incorporation: '',
    company_description: '',
    has_entity_structure: '',
    has_aml_kyc: '',
    has_investor_reporting: '',
    why_governance: '',
    hear_about: '',
    biggest_governance_challenge: '',
    target_investors: '',
    has_cofounder: '',
    can_commit_hours: '',
    can_attend_weekly: '',
    fee_commitment: '',
    scholarship_track: false,
    referred_by_code: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(null); // {id, referral_code, duplicate}
  const [referrer, setReferrer] = useState(null); // {valid, referrer_first_name, referrer_company}

  // Read ?ref=CODE from URL on mount, validate against backend
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get('ref') || '').trim().toUpperCase();
    if (!ref || ref.length > 16) return;
    setForm((p) => ({ ...p, referred_by_code: ref }));
    axios
      .get(`${API}/cohort/referrals/lookup/${encodeURIComponent(ref)}`)
      .then(({ data }) => {
        if (data?.valid) setReferrer(data);
      })
      .catch(() => { /* silent — invalid codes drop quietly */ });
  }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const updateRadio = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const required = [
    'founder_name', 'founder_email', 'whatsapp', 'country_residence', 'linkedin_url',
    'company_name', 'industry', 'company_stage', 'company_description',
    'why_governance', 'hear_about', 'biggest_governance_challenge', 'target_investors',
    'can_commit_hours', 'can_attend_weekly', 'fee_commitment',
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const missing = required.filter((k) => !String(form[k] || '').trim());
    if (missing.length) {
      setError(`Please complete the required fields: ${missing.join(', ').replace(/_/g, ' ')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.founder_email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        // Map company_description into both new and legacy fields so the AI scorer has substance
        one_liner: (form.company_description || '').slice(0, 240),
        problem: form.biggest_governance_challenge || form.company_description,
        solution: form.company_description,
        why_uae: `Targeting ${form.target_investors || 'UAE & international'} investors. ${form.why_governance || ''}`.trim(),
        funding_stage: form.company_stage,
        team_size: form.has_cofounder === 'Yes' ? 2 : 1,
        region: form.country_residence,
      };
      const { data } = await axios.post(`${API}/cohort/applications`, payload);
      setSubmitted({ id: data?.id, duplicate: !!data?.duplicate, referral_code: data?.referral_code });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Submission failed. Please try again or email accelerator@skiasiapacific.com.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const refLink = submitted.referral_code
      ? `${typeof window !== 'undefined' ? window.location.origin : 'https://skiaxis.com'}/apply?ref=${submitted.referral_code}`
      : null;
    return (
      <div className="app-root" data-testid="cohort-application-success">
        <style>{STYLES}</style>
        <Header />
        <div className="app-shell">
          <div className="app-success">
            <div className="app-success-icn"><CheckCircle2 className="h-7 w-7" /></div>
            <h1 className="app-success-h">Application <em>received</em>.</h1>
            <p className="app-success-d">
              Thank you for applying to SKI Axis Cohort 1. {submitted.duplicate ? "We've already received an application from this email — we'll be in touch with the same response timeline." : "Your application has been logged and will be evaluated through our governance-readiness framework."} You'll hear back within <strong>7 working days</strong>. For urgent questions, email <a href="mailto:accelerator@skiasiapacific.com" style={{ color: '#C8A84B' }}>accelerator@skiasiapacific.com</a>.
            </p>

            {/* BRING YOUR COHORT — referral link */}
            {refLink && (
              <div className="app-byc-card" data-testid="success-byc-card">
                <div className="app-byc-eb">Bring Your Cohort · AED 500 / referral</div>
                <h3 className="app-byc-h">Refer another founder. <em>Earn AED 500</em> off your fee.</h3>
                <p className="app-byc-d">Share your unique referral link below. For every founder you bring who is accepted into Cohort 1, AED 500 is credited to your fee — no cap.</p>
                <div className="app-byc-link">
                  <code data-testid="success-referral-link">{refLink}</code>
                  <button
                    type="button"
                    className="app-byc-copy"
                    data-testid="success-referral-copy"
                    onClick={() => {
                      navigator.clipboard?.writeText(refLink);
                    }}
                  >
                    Copy
                  </button>
                </div>
                <p className="app-byc-foot">
                  Track your referrals any time at <Link to="/refer" style={{ color: '#C8A84B', textDecoration: 'none', fontWeight: 700 }}>skiaxis.com/refer</Link>
                </p>
              </div>
            )}

            <Link to="/" className="app-success-cta" data-testid="success-back-home">
              Back to Homepage <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app-root" data-testid="cohort-application-page">
      <style>{STYLES}</style>
      <Header />

      {/* URGENCY */}
      <div className="app-urgent" data-testid="cohort-urgency-bar">
        <em>⚡ Cohort 1 Applications Open</em> · 20 Seats Only · Closes 30 June 2026
      </div>

      <div className="app-shell">
        {/* HERO */}
        <div className="app-hero">
          <div className="app-eyebrow">Cohort 1 · July 2026 · Dubai, UAE</div>
          <h1 className="app-h1">Apply for <em>SKI Axis</em> Cohort 1.</h1>
          <p className="app-subline">
            20 seats. 12-week governance architecture programme. Applications close <strong>30 June 2026</strong>.
            Every founder leaves with a fully-built three-layer governance framework — the institutional infrastructure
            sovereign investors and family offices require before deploying capital.
          </p>
          {referrer && (
            <div className="app-ref-pill" data-testid="referrer-pill" style={{ marginTop: 22 }}>
              <span className="app-ref-pill-eb">Referred by</span>
              <span className="app-ref-pill-name">
                {referrer.referrer_first_name}
                {referrer.referrer_company ? <em> · {referrer.referrer_company}</em> : null}
              </span>
            </div>
          )}
        </div>

        {/* WHY APPLY */}
        <div className="app-why">
          <div className="app-why-card">
            <div className="app-why-icn"><Globe2 className="h-4 w-4" /></div>
            <div>
              <p className="app-why-t">Free UAE incorporation consultancy via IFZA <span style={{ opacity: 0.75, fontWeight: 400 }}>(advisory only · licence &amp; visa fees not included)</span></p>
            </div>
          </div>
          <div className="app-why-card">
            <div className="app-why-icn"><Sparkles className="h-4 w-4" /></div>
            <div>
              <p className="app-why-t">Direct access to sovereign & institutional investor network</p>
            </div>
          </div>
          <div className="app-why-card">
            <div className="app-why-icn"><Shield className="h-4 w-4" /></div>
            <div>
              <p className="app-why-t">Graduate with full three-layer governance architecture built</p>
            </div>
          </div>
        </div>

        {/* FORM */}
        <form onSubmit={onSubmit} className="app-form" data-testid="cohort-application-form">
          {error && (
            <div className="app-err" data-testid="cohort-form-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* SECTION 1 — ABOUT YOU */}
          <div className="app-section">
            <div className="app-sec-num">i.</div>
            <h2 className="app-sec-h">About you</h2>
            <div className="app-row">
              <div className="app-fld">
                <label className="app-lbl">Full name <em>*</em></label>
                <input className="app-input" data-testid="fld-founder_name" value={form.founder_name} onChange={(e) => update('founder_name', e.target.value)} required />
              </div>
              <div className="app-fld">
                <label className="app-lbl">Email address <em>*</em></label>
                <input type="email" className="app-input" data-testid="fld-founder_email" value={form.founder_email} onChange={(e) => update('founder_email', e.target.value)} required />
              </div>
            </div>
            <div className="app-row">
              <div className="app-fld">
                <label className="app-lbl">WhatsApp number <em>*</em></label>
                <input className="app-input" placeholder="+971 ..." data-testid="fld-whatsapp" value={form.whatsapp} onChange={(e) => update('whatsapp', e.target.value)} required />
              </div>
              <div className="app-fld">
                <label className="app-lbl">Country of residence <em>*</em></label>
                <input className="app-input" data-testid="fld-country_residence" value={form.country_residence} onChange={(e) => update('country_residence', e.target.value)} required />
              </div>
            </div>
            <div className="app-row-1">
              <div className="app-fld">
                <label className="app-lbl">LinkedIn profile URL <em>*</em></label>
                <input className="app-input" placeholder="https://linkedin.com/in/..." data-testid="fld-linkedin_url" value={form.linkedin_url} onChange={(e) => update('linkedin_url', e.target.value)} required />
              </div>
            </div>
          </div>

          {/* SECTION 2 — ABOUT YOUR COMPANY */}
          <div className="app-section">
            <div className="app-sec-num">ii.</div>
            <h2 className="app-sec-h">About your company</h2>
            <div className="app-row">
              <div className="app-fld">
                <label className="app-lbl">Company name <em>*</em></label>
                <span className="app-help">Or working title if pre-incorporation</span>
                <input className="app-input" data-testid="fld-company_name" value={form.company_name} onChange={(e) => update('company_name', e.target.value)} required />
              </div>
              <div className="app-fld">
                <label className="app-lbl">Industry / sector <em>*</em></label>
                <input className="app-input" placeholder="AI, FinTech, Governance Tech …" data-testid="fld-industry" value={form.industry} onChange={(e) => update('industry', e.target.value)} required />
              </div>
            </div>
            <div className="app-row">
              <div className="app-fld">
                <label className="app-lbl">Company stage <em>*</em></label>
                <RadioGroup name="company_stage" options={['Idea', 'MVP', 'Early Revenue', 'Growth']} value={form.company_stage} onChange={updateRadio} />
              </div>
              <div className="app-fld">
                <label className="app-lbl">Country of incorporation</label>
                <span className="app-help">Or planned</span>
                <input className="app-input" data-testid="fld-country_incorporation" value={form.country_incorporation} onChange={(e) => update('country_incorporation', e.target.value)} />
              </div>
            </div>
            <div className="app-row-1">
              <div className="app-fld">
                <label className="app-lbl">Brief description of what you are building <em>*</em></label>
                <span className="app-help">200 words max</span>
                <textarea className="app-area" data-testid="fld-company_description" maxLength={2400} value={form.company_description} onChange={(e) => update('company_description', e.target.value)} required />
              </div>
            </div>
          </div>

          {/* SECTION 3 — GOVERNANCE READINESS */}
          <div className="app-section">
            <div className="app-sec-num">iii.</div>
            <h2 className="app-sec-h">Governance readiness</h2>
            <div className="app-row-1">
              <div className="app-fld">
                <label className="app-lbl">Do you have a formal entity structure?</label>
                <RadioGroup name="has_entity_structure" options={['Yes', 'No', 'Partially']} value={form.has_entity_structure} onChange={updateRadio} />
              </div>
              <div className="app-fld">
                <label className="app-lbl">Have you completed AML/KYC compliance setup?</label>
                <RadioGroup name="has_aml_kyc" options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }, { value: 'Want-to', label: 'No — but I want to' }]} value={form.has_aml_kyc} onChange={updateRadio} />
              </div>
              <div className="app-fld">
                <label className="app-lbl">Do you have an investor reporting framework?</label>
                <RadioGroup name="has_investor_reporting" options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }, { value: 'What-is-that', label: 'What is that?' }]} value={form.has_investor_reporting} onChange={updateRadio} />
              </div>
              <div className="app-fld">
                <label className="app-lbl">Why does governance matter to your business right now? <em>*</em></label>
                <span className="app-help">150 words max</span>
                <textarea className="app-area" data-testid="fld-why_governance" maxLength={1800} value={form.why_governance} onChange={(e) => update('why_governance', e.target.value)} required />
              </div>
            </div>
          </div>

          {/* SECTION 4 — SKI AXIS FIT */}
          <div className="app-section">
            <div className="app-sec-num">iv.</div>
            <h2 className="app-sec-h">SKI Axis fit</h2>
            <div className="app-row-1">
              <div className="app-fld">
                <label className="app-lbl">How did you hear about SKI Axis? <em>*</em></label>
                <input className="app-input" data-testid="fld-hear_about" value={form.hear_about} onChange={(e) => update('hear_about', e.target.value)} required />
              </div>
              <div className="app-fld">
                <label className="app-lbl">What is the single biggest governance challenge your company faces? <em>*</em></label>
                <span className="app-help">100 words max</span>
                <textarea className="app-area" data-testid="fld-biggest_governance_challenge" maxLength={1200} value={form.biggest_governance_challenge} onChange={(e) => update('biggest_governance_challenge', e.target.value)} required />
              </div>
              <div className="app-fld">
                <label className="app-lbl">Are you targeting UAE investors, international, or both? <em>*</em></label>
                <RadioGroup name="target_investors" options={[{ value: 'UAE', label: 'UAE' }, { value: 'International', label: 'International' }, { value: 'Both', label: 'Both' }]} value={form.target_investors} onChange={updateRadio} />
              </div>
              <div className="app-fld">
                <label className="app-lbl">Do you have a co-founder?</label>
                <RadioGroup name="has_cofounder" options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }, { value: 'Looking', label: 'Looking for one' }]} value={form.has_cofounder} onChange={updateRadio} />
              </div>
            </div>
          </div>

          {/* SECTION 5 — COMMITMENT */}
          <div className="app-section">
            <div className="app-sec-num">v.</div>
            <h2 className="app-sec-h">Commitment</h2>
            <div className="app-row-1">
              <div className="app-fld">
                <label className="app-lbl">Can you commit 15–20 hours per week to the programme? <em>*</em></label>
                <RadioGroup name="can_commit_hours" options={['Yes', 'No', 'Partially']} value={form.can_commit_hours} onChange={updateRadio} />
              </div>
              <div className="app-fld">
                <label className="app-lbl">Are you able to attend weekly live governance sessions (online)? <em>*</em></label>
                <RadioGroup name="can_attend_weekly" options={['Yes', 'No']} value={form.can_attend_weekly} onChange={updateRadio} />
              </div>
              <div className="app-fld">
                <label className="app-lbl">Programme fee: AED 6,900. Are you able to commit? <em>*</em></label>
                <RadioGroup name="fee_commitment" options={[{ value: 'Yes', label: 'Yes' }, { value: 'Need-to-discuss', label: 'I need to discuss options' }]} value={form.fee_commitment} onChange={updateRadio} />
              </div>
            </div>

            {/* SCHOLARSHIP */}
            <label className="app-schol" data-testid="cohort-scholarship-toggle">
              <input type="checkbox" checked={form.scholarship_track} onChange={(e) => update('scholarship_track', e.target.checked)} />
              <div>
                <p className="app-schol-t">She Built This × SKI Axis Scholarship</p>
                <p className="app-schol-d">
                  I identify as a <strong>woman founder</strong> and would like to be considered for the She Built This × SKI Axis scholarship track —
                  <strong> 5 fully-funded seats per cohort</strong>.
                </p>
              </div>
            </label>
          </div>

          {/* SUBMIT */}
          <button type="submit" className="app-submit" disabled={submitting} data-testid="cohort-submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScrollText className="h-4 w-4" />}
            {submitting ? 'Submitting…' : 'Submit my application'}
            {!submitting && <ArrowRight className="h-4 w-4" />}
          </button>

          <p className="app-foot">
            Applications are reviewed on a rolling basis. You will hear from us within <strong>7 working days</strong> of submission.<br />
            Questions? Email <a href="mailto:accelerator@skiasiapacific.com">accelerator@skiasiapacific.com</a>
          </p>
        </form>

        {/* WHAT HAPPENS NEXT */}
        <div className="app-next" data-testid="cohort-whats-next">
          <h3 className="app-next-h">What <em>happens next</em></h3>
          <div className="app-next-grid">
            <div className="app-next-step">
              <div className="app-next-num">i.</div>
              <h4 className="app-next-t">AI Assessment</h4>
              <p className="app-next-d">Your application is scored across entrepreneurial and governance dimensions through our proprietary readiness framework.</p>
            </div>
            <div className="app-next-step">
              <div className="app-next-num">ii.</div>
              <h4 className="app-next-t">Founder Interview</h4>
              <p className="app-next-d">Shortlisted founders join a 30-minute video call with Dr. Neha Dewan to align on programme fit and ambition.</p>
            </div>
            <div className="app-next-step">
              <div className="app-next-num">iii.</div>
              <h4 className="app-next-t">Cohort Offer</h4>
              <p className="app-next-d">Accepted founders receive a formal offer letter, onboarding pack, and the Cohort 1 commitment agreement.</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
